"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ArrowRight, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel"

// Hero images array
const heroImages = [
  {
    src: "https://res.cloudinary.com/dtnerqsid/image/upload/f_auto,q_auto/v1769363891/zest-and-zeal/products/1001331452_1769363891275.jpg",
    alt: "Gourmet burger with fresh ingredients",
  },
  {
    src: "https://res.cloudinary.com/dtnerqsid/image/upload/f_auto,q_auto/v1769360238/zest-and-zeal/products/1001331319_1769360237999.jpg",
    alt: "Artisan bread and pastries",
  },
  {
    src: "https://res.cloudinary.com/dtnerqsid/image/upload/f_auto,q_auto/v1769360342/zest-and-zeal/products/1001331368_1769360342280.jpg",
    alt: "Fresh organic salad bowl",
  },
  {
    src: "https://res.cloudinary.com/dtnerqsid/image/upload/f_auto,q_auto/v1769360181/zest-and-zeal/products/1001331303_1769360181233.jpg",
    alt: "Gourmet coffee and dessert",
  },
  {
    src: "https://res.cloudinary.com/dtnerqsid/image/upload/f_auto,q_auto/v1769365528/zest-and-zeal/products/1001331547_1769365528678.jpg",
    alt: "Gourmet coffee and dessert",
  },
  {
    src: "https://res.cloudinary.com/dtnerqsid/image/upload/f_auto,q_auto/v1769369556/zest-and-zeal/products/1001331698_1769369556698.jpg",
    alt: "Gourmet coffee and dessert",
  },
  {
    src: "https://res.cloudinary.com/dtnerqsid/image/upload/f_auto,q_auto/v1769370584/zest-and-zeal/products/1001331705_1769370584147.jpg",
    alt: "Gourmet coffee and dessert",
  },
  {
    src: "https://res.cloudinary.com/dtnerqsid/image/upload/f_auto,q_auto/v1769362193/zest-and-zeal/products/1001331291_1769362193623.jpg",
    alt: "Gourmet coffee and dessert",
  },
  {
    src: "https://res.cloudinary.com/dtnerqsid/image/upload/f_auto,q_auto/v1769362750/zest-and-zeal/products/1001331448_1769362750554.jpg",
    alt: "Gourmet coffee and dessert",
  },
  {
    src: "https://res.cloudinary.com/dtnerqsid/image/upload/f_auto,q_auto/v1769361141/zest-and-zeal/products/1001320927_1769361141535.jpg",
    alt: "Gourmet coffee and dessert",
  },
]

export default function Hero() {
  const [api, setApi] = useState<any>(null)
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    if (!api) return

    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])

  // Auto-play functionality
  useEffect(() => {
    if (!api) return

    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext()
      } else {
        api.scrollTo(0)
      }
    }, 5000) // Change slide every 5 seconds

    return () => clearInterval(interval)
  }, [api])

  return (
    <section className="relative overflow-hidden bg-background min-h-[110svh] md:min-h-screen">
      {/* Full Width & Height Image Slider */}
      <div className="absolute inset-0">
        <Carousel
          setApi={setApi}
          opts={{
            align: "start",
            loop: true,
          }}
          className="h-[110svh] w-full md:h-full"
        >
          <CarouselContent className="h-[110svh] md:h-full">
            {heroImages.map((image, index) => (
              <CarouselItem key={index} className="h-[110svh] pl-0 md:h-full">
                <div className="relative h-[110svh] w-full overflow-hidden md:h-full">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={index}
                      src={image.src}
                      alt={image.alt}
                      initial={{ opacity: 0, scale: 1.1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.5 }}
                      className="h-full w-full object-cover"
                    />
                  </AnimatePresence>
                  {/* Dark overlay for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60 pointer-events-none" />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          {/* <CarouselPrevious className="left-4 border-border bg-card/80 backdrop-blur-sm hover:bg-card z-20" />
          <CarouselNext className="right-4 border-border bg-card/80 backdrop-blur-sm hover:bg-card z-20" /> */}
        </Carousel>
      </div>

      {/* Content Overlay */}
      <div className="relative z-10 container mx-auto px-4 py-20 md:py-32 min-h-[100svh] md:min-h-screen flex items-center">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 w-full">
          {/* Left Content */}
          <div className="flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6 inline-flex items-center gap-2 self-start rounded-full border border-border/50 bg-card/80 backdrop-blur-sm px-4 py-2"
            >
              <Sparkles className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-medium text-foreground">Lagos’ Best Asun & Suya Rice</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-6 text-balance text-5xl font-bold tracking-tight text-foreground md:text-6xl lg:text-7xl drop-shadow-lg"
            >
              Hot & Smoky.{" "}
              <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                <br></br>
                Delivered in 40–60 Mins.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8 text-pretty text-lg leading-relaxed text-foreground/90 md:text-xl drop-shadow-md"
            >
              Savor freshly tossed, authentic Asun Rice and spicy Suya Rice, packed with rich fire-roasted flavor. Available in individual daily portions and large bulk party bowls—cooked fresh and delivered fast straight to your doorstep.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col gap-4 sm:flex-row"
            >
              <Button size="lg" className="group text-base" onClick={() => window.location.href = '/our-menu'}>
                VIEW THE FULL MENU
                <motion.div
                  className="ml-2"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Number.POSITIVE_INFINITY }}
                >
                  <ArrowRight className="h-5 w-5" />
                </motion.div>
              </Button>
              <Button size="lg" variant="outline" className="text-base bg-card/80 backdrop-blur-sm border-border/50" onClick={() => window.location.href = '/our-menu'}>
                Order Now
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-12 flex items-center gap-8"
            >
              <div>
                <div className="text-xl font-bold text-foreground drop-shadow-md">40–60 Min Delivery</div>
                <div className="text-sm text-foreground/80">Fresh, Fast & Steaming Hot</div>
              </div>
              <div>
                <div className="text-xl font-bold text-foreground drop-shadow-md">100% Online Kitchen</div>
                <div className="text-sm text-foreground/80">Cooked Fresh on Order</div>
              </div>
              <div>
                <div className="text-xl font-bold text-foreground drop-shadow-md">Single & Bulk Bowls</div>
                <div className="text-sm text-foreground/80">Daily Craving or Event Trays</div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex justify-center gap-2">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => api?.scrollTo(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              current === index
                ? "w-8 bg-primary"
                : "w-2 bg-white/30 hover:bg-white/50"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Floating Badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="absolute right-8 top-1/2 -translate-y-1/2 z-20 rounded-2xl border border-border/50 bg-card/80 backdrop-blur-sm p-6 shadow-xl hidden lg:block"
      >
        <div className="text-center">
          <div className="mb-2 text-3xl font-bold text-foreground">100% Online Kitchen</div>
          <div className="text-sm text-foreground/80">Cooked Fresh on Order</div>
        </div>
      </motion.div>
    </section>
  )
}

